/**
 * The four rep-facing Gong states (Gong reconcile plan D3 / D9 / D10) and the
 * ONE Gong-status vocabulary both the pure `decide()`
 * (`src/services/gong-reconcile/decide.ts`) and the derived state read.
 *
 * `FLOW_STATUS_NORMALIZATION_MAP` and `GongProbeStatusSchema` derive their
 * vocabulary from `SIGNAL_ACTION_STATUS_ORDER`, which spreads
 * `GONG_FLOW_END_STATUSES` from this module; this module remains the single
 * source of the wider Gong-status vocabulary and of the four rep-facing
 * states.
 */

/**
 * Gong `flowInstanceStatus` strings that mean the enrollment is over.
 * Matched case-insensitively. Any status starting with `Finished` is ALSO an
 * end status (`GONG_FLOW_FINISHED_PREFIX`) — Gong has several Finished
 * variants.
 *
 * Source: the prod Salesforce describe of
 * `Gong__Engage_Flow__c.Gong__Engage_Flow_Status__c` (2026-10-28); canonical
 * spelling is that picklist's. Gong's own API wording is still unrecorded —
 * `gong_flow_probe.gong_status` stores it raw, and matching is
 * case-insensitive, so a casing difference is harmless. A string not here
 * normalizes to `Unknown` → `fix_in_gong` and is never closed, so a miss is
 * visible, not destructive.
 *
 * This is the DISPLAY set (rep state `done`). Reconcile may close a row only
 * on the narrower `GONG_FLOW_CLOSE_STATUSES`.
 */
export const GONG_FLOW_END_STATUSES = [
  'Bounced',
  'To-do expired',
  'Opted Out',
  'Removed (other)',
  'No license',
  'Person deleted',
  'Flow deleted',
] as const;

/** Prefix (case-insensitive) that marks every Gong "Finished…" variant as ended. */
export const GONG_FLOW_FINISHED_PREFIX = 'Finished';

/**
 * The end statuses on which reconcile may CLOSE the Terra row (plus every
 * `Finished…` variant) — a subset of `GONG_FLOW_END_STATUSES`. The other end
 * statuses (`Removed (other)`, `No license`, `Person deleted`, `Flow
 * deleted`) render as `done` but never auto-close the row.
 */
export const GONG_FLOW_CLOSE_STATUSES = ['Bounced', 'To-do expired', 'Opted Out'] as const satisfies readonly (typeof GONG_FLOW_END_STATUSES)[number][];

/** True when a raw Gong status lets reconcile close the row: any `Finished…` variant or a `GONG_FLOW_CLOSE_STATUSES` member, case-insensitively. */
export function isGongCloseStatus(raw: string | null): boolean {
  if (raw === null) return false;
  const lower = raw.trim().toLowerCase();
  if (lower.startsWith(GONG_FLOW_FINISHED_PREFIX.toLowerCase())) return true;
  return GONG_FLOW_CLOSE_STATUSES.some((s) => s.toLowerCase() === lower);
}

/** What a raw Gong status string means for reconcile and the rep. */
export type NormalizedGongFlowStatus = 'Running' | 'Paused' | 'Pending' | 'Ended' | 'Unknown';

/**
 * Live (non-ended) statuses, keyed by the LOWER-CASED raw string. `In
 * Progress` is Salesforce's `Gong__Engage_Flow__c` spelling of Running; it is
 * kept so a mirror-sourced string normalizes the same way the arm's map does.
 */
export const GONG_FLOW_LIVE_STATUS_MAP: Readonly<Record<string, 'Running' | 'Paused' | 'Pending'>> = {
  running: 'Running',
  'in progress': 'Running',
  paused: 'Paused',
  pending: 'Pending',
};

/**
 * Raw Gong status → normalized status. `null` on an enrolled observation is
 * `Running` (same rule as D10's explicit `WHEN column IS NULL AND enrolled`).
 * Anything unrecognized is `Unknown` — never `Running` (the arm's `ELSE
 * 'Running'` catch-all is exactly what this replaces).
 */
export function normalizeGongFlowStatus(raw: string | null): NormalizedGongFlowStatus {
  if (raw === null) return 'Running';
  const lower = raw.trim().toLowerCase();
  const live = GONG_FLOW_LIVE_STATUS_MAP[lower];
  if (live) return live;
  if (lower.startsWith(GONG_FLOW_FINISHED_PREFIX.toLowerCase())) return 'Ended';
  if (GONG_FLOW_END_STATUSES.some((s) => s.toLowerCase() === lower)) return 'Ended';
  return 'Unknown';
}


/**
 * The four rep-facing states, in display order. `done` rows are hidden. The
 * one source for the `gong_state` wire enum (`SignalRowSchema`) and the
 * `gong_state` filter picklist (`SIGNAL_DISPLAY_FILTER_FIELDS`).
 */
export const REP_STATES = ['do_it_now', 'waiting', 'fix_in_gong', 'done'] as const;
export type RepState = (typeof REP_STATES)[number];

/**
 * The stored facts one `gong_flow_probe` row carries (U4 columns), or `null`
 * when there is no probe observation for the row yet (the LEFT JOIN found
 * nothing).
 */
export interface RepStateFacts {
  /** `gong_flow_probe.enrolled` — `false` means Gong did not list the enrollment. */
  enrolled: boolean;
  /** `gong_flow_probe.gong_status` — Gong's raw `flowInstanceStatus`. */
  gongStatus: string | null;
  /** `gong_flow_probe.fix_in_gong_after` — precomputed in TS by `decide()`. */
  fixInGongAfter: Date | null;
}

/**
 * The Signals row columns the derivation reads, named after the
 * `terra_signals` view columns so the TS and SQL paths read the SAME values:
 * `action_state`, `signal_tactic` (= `gef.step_type`), `due_date` (=
 * `gef.current_flow_task_due_date`), `step_number`.
 *
 * "No SF record" (no `Gong__Engage_Flow__c` mirrored for the row) is the
 * view's own observable: all three Engage-Flow-sourced columns NULL. The
 * list route joins `gef` and `p` too (flow-signals-list.ts
 * SIGNAL_LIST_JOINS); this three-NULL definition is the one both the view
 * and the list route can share.
 */
export interface RepStateRow {
  actionState: string | null;
  signalTactic: string | null;
  dueDate: Date | null;
  stepNumber: number | null;
  /**
   * `signal_status` — the arm's status text (the probe's when one exists, else the
   * Engage Flow mirror's). Read ONLY by the no-probe mirror rules, where it is the
   * mirror's own status; `null` when the caller has none (e.g. the reconcile shell,
   * which reads no mirror status — those two rules cannot fire there).
   */
  mirrorStatus: string | null;
}

/** `action_state` values that mean the row is closed. */
export const REP_STATE_CLOSED_ACTION_STATES = ['Completed', 'Archived', 'Expired'] as const;

/** Substring (case-insensitive) in `signal_tactic` that marks an auto-send step. Same rule as `STEP_NOT_AUTO`. */
export const AUTO_STEP_MARKER = 'auto';

/**
 * One atomic predicate. Every rule in `REP_STATE_RULES` is an AND of these,
 * and each kind has exactly one TS evaluator (`evalCondition`) and one SQL
 * emitter (`conditionSql`) — so the rule TABLE (order, states, combinations)
 * is the single source both paths are generated from.
 */
export type RepStateCondition =
  | { kind: 'action_state_in'; values: readonly string[] }
  | { kind: 'no_probe' }
  | { kind: 'not_enrolled' }
  | { kind: 'no_sf_record' }
  | { kind: 'status_in'; values: readonly NormalizedGongFlowStatus[] }
  | { kind: 'mirror_status_in'; values: readonly NormalizedGongFlowStatus[] }
  | { kind: 'auto_step' }
  | { kind: 'due_now' }
  | { kind: 'past_fix_in_gong_after' };

export interface RepStateRule {
  /** Stable id for tests / debugging. */
  id: string;
  /** Every condition must hold. An empty list is the catch-all (last rule only). */
  when: readonly RepStateCondition[];
  state: RepState;
}

const LIVE_RUNNING: readonly NormalizedGongFlowStatus[] = ['Running'];

/**
 * D9, first match wins. The final rule has no conditions and becomes the
 * SQL `ELSE`. Ordering notes:
 * - Closed and Snoozed come first (Snoozed rows are already excluded by
 *   `action_state` filters; their value is `waiting`).
 * - No probe row → mirror-only, as today's STATUS_CASE fallback: manual step
 *   due → do it now; auto or not due → waiting; no SF record either → do it
 *   now (fresh rows are not hidden; the page-load live check verifies them).
 * - Owner ≠ rep never changes the state (D9 last bullet) — the owner is
 *   projected for display separately, so it is not a condition here.
 */
export const REP_STATE_RULES: readonly RepStateRule[] = [
  { id: 'closed', when: [{ kind: 'action_state_in', values: REP_STATE_CLOSED_ACTION_STATES }], state: 'done' },
  { id: 'snoozed', when: [{ kind: 'action_state_in', values: ['Snoozed'] }], state: 'waiting' },
  // The mirror's own status protects a no-probe row: a Paused flow needs fixing in Gong, an
  // ended one is done. Deliberately NO mirror-Unknown rule ('No flow' normalises to Unknown).
  { id: 'mirror_paused', when: [{ kind: 'no_probe' }, { kind: 'mirror_status_in', values: ['Paused'] }], state: 'fix_in_gong' },
  { id: 'mirror_ended', when: [{ kind: 'no_probe' }, { kind: 'mirror_status_in', values: ['Ended'] }], state: 'done' },
  { id: 'mirror_no_sf_record', when: [{ kind: 'no_probe' }, { kind: 'no_sf_record' }], state: 'do_it_now' },
  { id: 'mirror_auto_step', when: [{ kind: 'no_probe' }, { kind: 'auto_step' }], state: 'waiting' },
  { id: 'mirror_manual_due', when: [{ kind: 'no_probe' }, { kind: 'due_now' }], state: 'do_it_now' },
  { id: 'mirror_not_due', when: [{ kind: 'no_probe' }], state: 'waiting' },
  { id: 'not_enrolled', when: [{ kind: 'not_enrolled' }], state: 'waiting' },
  { id: 'gong_ended', when: [{ kind: 'status_in', values: ['Ended'] }], state: 'done' },
  { id: 'gong_unknown', when: [{ kind: 'status_in', values: ['Unknown'] }], state: 'fix_in_gong' },
  { id: 'gong_paused', when: [{ kind: 'status_in', values: ['Paused'] }], state: 'fix_in_gong' },
  { id: 'pending_past_grace', when: [{ kind: 'status_in', values: ['Pending'] }, { kind: 'past_fix_in_gong_after' }], state: 'fix_in_gong' },
  { id: 'pending', when: [{ kind: 'status_in', values: ['Pending'] }], state: 'waiting' },
  { id: 'running_no_sf_past_grace', when: [{ kind: 'status_in', values: LIVE_RUNNING }, { kind: 'no_sf_record' }, { kind: 'past_fix_in_gong_after' }], state: 'fix_in_gong' },
  { id: 'running_no_sf', when: [{ kind: 'status_in', values: LIVE_RUNNING }, { kind: 'no_sf_record' }], state: 'do_it_now' },
  { id: 'running_auto_past_grace', when: [{ kind: 'status_in', values: LIVE_RUNNING }, { kind: 'auto_step' }, { kind: 'past_fix_in_gong_after' }], state: 'fix_in_gong' },
  { id: 'running_auto', when: [{ kind: 'status_in', values: LIVE_RUNNING }, { kind: 'auto_step' }], state: 'waiting' },
  { id: 'running_manual_due', when: [{ kind: 'status_in', values: LIVE_RUNNING }, { kind: 'due_now' }], state: 'do_it_now' },
  { id: 'otherwise', when: [], state: 'waiting' },
];

function hasNoSfRecord(row: RepStateRow): boolean {
  return row.signalTactic === null && row.dueDate === null && row.stepNumber === null;
}

function evalCondition(c: RepStateCondition, facts: RepStateFacts | null, row: RepStateRow, now: Date): boolean {
  switch (c.kind) {
    case 'action_state_in':
      return row.actionState !== null && c.values.includes(row.actionState);
    case 'no_probe':
      return facts === null;
    case 'not_enrolled':
      return facts !== null && facts.enrolled === false;
    case 'no_sf_record':
      return hasNoSfRecord(row);
    case 'status_in':
      return facts !== null && c.values.includes(normalizeGongFlowStatus(facts.gongStatus));
    case 'mirror_status_in':
      return row.mirrorStatus !== null && c.values.includes(normalizeGongFlowStatus(row.mirrorStatus));
    case 'auto_step':
      return row.signalTactic !== null && row.signalTactic.toLowerCase().includes(AUTO_STEP_MARKER);
    case 'due_now':
      return row.dueDate !== null && row.dueDate.getTime() <= now.getTime();
    case 'past_fix_in_gong_after':
      return facts !== null && facts.fixInGongAfter !== null && now.getTime() > facts.fixInGongAfter.getTime();
  }
}

/** The first `REP_STATE_RULES` entry whose conditions all hold (the rule `deriveRepState` applies). */
export function matchRepStateRule(facts: RepStateFacts | null, row: RepStateRow, now: Date): RepStateRule {
  for (const rule of REP_STATE_RULES) {
    if (rule.when.every((c) => evalCondition(c, facts, row, now))) return rule;
  }
  // Unreachable: the last rule has no conditions. Kept for the type checker.
  throw new Error('REP_STATE_RULES must end with a catch-all rule');
}

/** D9: the rep-facing state for one row. First matching `REP_STATE_RULES` entry wins. */
export function deriveRepState(facts: RepStateFacts | null, row: RepStateRow, now: Date): RepState {
  return matchRepStateRule(facts, row, now).state;
}


function sqlLiteral(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

function sqlList(values: readonly string[]): string {
  return values.map(sqlLiteral).join(', ');
}

/** The normaliser CASE over any status column (the probe's `gong_status`, or the row's `signal_status`). */
function normalizedStatusColumnSql(col: string): string {
  const norm = `lower(btrim(${col}))`;
  const liveWhens = Object.entries(GONG_FLOW_LIVE_STATUS_MAP)
    .map(([raw, normalized]) => `WHEN ${norm} = ${sqlLiteral(raw)} THEN ${sqlLiteral(normalized)}`)
    .join(' ');
  const endList = sqlList(GONG_FLOW_END_STATUSES.map((s) => s.toLowerCase()));
  return `(CASE WHEN ${col} IS NULL THEN 'Running' ${liveWhens} WHEN ${norm} LIKE ${sqlLiteral(`${GONG_FLOW_FINISHED_PREFIX.toLowerCase()}%`)} THEN 'Ended' WHEN ${norm} IN (${endList}) THEN 'Ended' ELSE 'Unknown' END)`;
}

/**
 * SQL twin of `evalCondition` for one condition kind. Exported so the unit
 * test pins each kind's text and the parity test can name the WHEN a case hits.
 */
export function conditionSql(c: RepStateCondition, p: string, ts: string): string {
  switch (c.kind) {
    case 'action_state_in':
      return `${ts}.action_state IN (${sqlList(c.values)})`;
    case 'no_probe':
      return `${p}.flow_instance_id IS NULL`;
    case 'not_enrolled':
      return `${p}.enrolled IS FALSE`;
    case 'no_sf_record':
      return `(${ts}.signal_tactic IS NULL AND ${ts}.due_date IS NULL AND ${ts}.step_number IS NULL)`;
    case 'status_in':
      return `(${p}.flow_instance_id IS NOT NULL AND ${normalizedStatusColumnSql(`${p}.gong_status`)} IN (${sqlList(c.values)}))`;
    case 'mirror_status_in':
      return `(${ts}.signal_status IS NOT NULL AND ${normalizedStatusColumnSql(`${ts}.signal_status`)} IN (${sqlList(c.values)}))`;
    case 'auto_step':
      return `${ts}.signal_tactic ILIKE ${sqlLiteral(`%${AUTO_STEP_MARKER}%`)}`;
    case 'due_now':
      return `${ts}.due_date <= now()`;
    case 'past_fix_in_gong_after':
      return `${p}.fix_in_gong_after < now()`;
  }
}

/**
 * `CASE WHEN … THEN '<state>' … ELSE '<catch-all state>' END`, generated
 * rule-by-rule from `REP_STATE_RULES` (pattern: the arm's
 * `normalizedFlowStatusCaseSql`). `probeAlias` is the `gong_flow_probe`
 * alias; `rowAlias` the Signals row alias (the list route's `ts`). Reads
 * only column comparisons and `now()` — the business-day grace is already
 * baked into `fix_in_gong_after` by `decide()`. Consumed by
 * `src/api/flow/flow-signals-list.ts` (the Signals list route).
 */
export function repStateCaseSql(probeAlias: string, rowAlias = 'ts'): string {
  const last = REP_STATE_RULES[REP_STATE_RULES.length - 1];
  if (!last || last.when.length > 0) throw new Error('REP_STATE_RULES must end with a catch-all rule');
  const whens = REP_STATE_RULES.slice(0, -1)
    .map((rule) => `WHEN ${rule.when.map((c) => conditionSql(c, probeAlias, rowAlias)).join(' AND ')} THEN ${sqlLiteral(rule.state)}`)
    .join(' ');
  return `CASE ${whens} ELSE ${sqlLiteral(last.state)} END`;
}
